import {
	listEnvironmentStatuses,
	listEnvironments,
	listProjects,
	type Environment,
	type EnvironmentStatus,
	type EnvironmentStatusEntry,
	type Project,
} from "@/lib/api";

export interface NewChatEnvironmentRequestLoaders {
	projects: () => Promise<Project[]>;
	environments: () => Promise<Environment[]>;
	statuses: () => Promise<EnvironmentStatusEntry[]>;
}

export interface NewChatEnvironmentRequestResults {
	projects: PromiseSettledResult<Project[]>;
	environments: PromiseSettledResult<Environment[]>;
	statuses: PromiseSettledResult<EnvironmentStatusEntry[]>;
}

const DEFAULT_LOADERS: NewChatEnvironmentRequestLoaders = {
	projects: listProjects,
	environments: listEnvironments,
	statuses: listEnvironmentStatuses,
};

/**
 * 新規チャットのプロジェクト・環境・状態を同時に取得し、3 件すべての結果を返す。
 * `Promise.allSettled` により、一部の API が失敗しても他の拒否済み Promise を未処理にしない。
 */
export function loadNewChatEnvironmentData(
	loaders: NewChatEnvironmentRequestLoaders = DEFAULT_LOADERS,
): Promise<NewChatEnvironmentRequestResults> {
	const requests = [
		callLoader(loaders.projects),
		callLoader(loaders.environments),
		callLoader(loaders.statuses),
	] as const;

	return Promise.allSettled(requests).then(([projects, environments, statuses]) => ({
		projects,
		environments,
		statuses,
	}));
}

function callLoader<T>(loader: () => Promise<T>): Promise<T> {
	try {
		const request = Promise.resolve(loader());
		void request.catch(() => undefined);
		return request;
	} catch (error: unknown) {
		const request = Promise.reject(error);
		void request.catch(() => undefined);
		return request;
	}
}

/**
 * 新規チャットの対象エージェントを返す。一覧に無いプロジェクト (アーカイブ済みなど) の環境は対象にしない。
 */
export function findNewChatEnvironment(
	projects: readonly Project[],
	environments: readonly Environment[],
	environmentId: string | null,
): Environment | null {
	const projectIds = new Set(projects.map((project) => project.id));
	return (
		environments.find(
			(environment) => environment.id === environmentId && projectIds.has(environment.projectId),
		) ?? null
	);
}

/** 起動・確保待ち・終了処理中など、待てば状態が変わるか。新規チャットのポーリング継続判定に使う。 */
export function isTransitioningEnvironmentStatus(status: EnvironmentStatus | undefined): boolean {
	return status === "pending" || status === "waitingForCapacity" || status === "shuttingDown";
}
